import type {ReactNode} from 'react';
import {parseSummaryDocument} from './summaryPresentation';
import type {SummaryPresentation} from './summaryPresentation';

interface SummaryResultProps {
    readonly summary: SummaryPresentation;
    readonly density?: 'regular' | 'compact';
}

function SummarySection({label, items}: {readonly label: string; readonly items: readonly ReactNode[]}) {
    if (items.length === 0) return null;
    return <section className="mt-3">
        <h6 className="font-medium text-ink-secondary mb-1">{label}</h6>
        <ul className="space-y-1 list-disc pl-4">{items.map((item, index) => <li key={index}>{item}</li>)}</ul>
    </section>;
}

export function SummaryResult({summary, density = 'regular'}: SummaryResultProps) {
    const {data, coverage} = summary;
    const document = parseSummaryDocument(data.summary_json);
    return <div className={`text-ink-muted-strong ${density === 'compact' ? 'text-xs' : 'text-sm'}`}>
        {document ? <>
            {data.message_count > 10 && <h6 className="font-semibold text-ink mb-2">
                Summary ({data.time_range}, {data.message_count} messages)
            </h6>}
            <p className="text-ink-secondary mb-2 whitespace-pre-wrap">{document.overallSummary}</p>
            {document.keyPoints.length > 0 && <ul className="space-y-1 list-disc pl-4">
                {document.keyPoints.map((point, index) => <li key={index}>{point}</li>)}
            </ul>}
            <SummarySection label="Topics" items={document.topics.map(topic => <>
                <strong>{topic.topic}</strong> — {topic.details}
            </>)}/>
            <SummarySection label="Decisions" items={document.decisions.map(decision => <>
                {decision.decision} (Participants: {decision.participants})
            </>)}/>
            <SummarySection label="Action Items" items={document.actionItems.map(item => <>
                {item.task} (Assignee: {item.assignee ?? 'Unassigned'})
            </>)}/>
            <SummarySection label="Announcements" items={document.announcements}/>
        </> : <p className="whitespace-pre-wrap">{data.summary_markdown}</p>}
        <p className="mt-3 text-xs text-ink-muted">
            {data.message_count} messages | Coverage: {data.range_display}
            {coverage?.endExclusive && '. The end is exclusive.'}
            {coverage?.limitedToMembership && " Coverage starts at the bot's latest join."}
            {(coverage?.unavailableEncryptedEvents ?? 0) > 0
                && ' Some encrypted messages were unavailable and were not included.'}
        </p>
    </div>;
}
